import { useState, useEffect, useRef } from 'react';

interface UseSmoothLoadingOptions {
  minLoadingTime?: number; // Minimum time to show loading state (ms)
  debounceTime?: number;   // Time to debounce rapid loading state changes (ms)
}

export function useSmoothLoading(
  isLoading: boolean,
  options: UseSmoothLoadingOptions = {}
) {
  const { minLoadingTime = 300, debounceTime = 100 } = options;
  
  const [smoothLoading, setSmoothLoading] = useState(false);
  const [showContent, setShowContent] = useState(!isLoading);
  
  const loadingStartTime = useRef<number | null>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = null;
    }
    
    if (isLoading) {
      // Start loading immediately if not already loading
      if (!smoothLoading) {
        loadingStartTime.current = Date.now();
        setSmoothLoading(true);
        setShowContent(false);
      }
    } else {
      // Debounce the end of loading to prevent flicker
      debounceTimeout.current = setTimeout(() => {
        const now = Date.now();
        const elapsedTime = loadingStartTime.current ? now - loadingStartTime.current : 0;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          setSmoothLoading(false);
          setShowContent(true);
          loadingStartTime.current = null;
        }, remainingTime);
      }, debounceTime);
    }
    
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [isLoading, minLoadingTime, debounceTime, smoothLoading]);
  
  return {
    isLoading: smoothLoading,
    showContent,
    // Utility for fade transitions
    contentClasses: showContent ? 'opacity-100' : 'opacity-0',
    loadingClasses: smoothLoading ? 'opacity-100' : 'opacity-0'
  };
}
